import React from 'react';
import styles from './Select.module.css';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type SelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  variant?: 'default' | 'compact';
};

const mergeClassNames = (...names: Array<string | undefined | false>): string =>
  names.filter(Boolean).join(' ');

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  className,
  triggerClassName,
  menuClassName,
  disabled = false,
  ariaLabel,
  placeholder = 'Select…',
  variant = 'default',
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const listboxId = React.useId();

  const enabledOptions = React.useMemo(
    () => options.filter(option => !option.disabled),
    [options],
  );

  const selectedOption = React.useMemo(
    () => options.find(option => option.value === value) ?? enabledOptions[0],
    [enabledOptions, options, value],
  );

  const selectedLabel = selectedOption?.label ?? placeholder;

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const selectedIndex = enabledOptions.findIndex(option => option.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [enabledOptions, isOpen, value]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const commitSelection = (nextValue: string) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  const toggleOpen = () => {
    if (disabled) {
      return;
    }

    setIsOpen(current => !current);
  };

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (enabledOptions.length === 0) {
        return;
      }

      setActiveIndex(prev => (prev + 1) % enabledOptions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (enabledOptions.length === 0) {
        return;
      }

      setActiveIndex(prev => (prev <= 0 ? enabledOptions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const activeOption = activeIndex >= 0 ? enabledOptions[activeIndex] : undefined;
      if (activeOption) {
        commitSelection(activeOption.value);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className={mergeClassNames(styles.root, className)} ref={rootRef}>
      <button
        type="button"
        className={mergeClassNames(
          styles.trigger,
          variant === 'compact' && styles.triggerCompact,
          isOpen && styles.triggerOpen,
          triggerClassName,
        )}
        onClick={toggleOpen}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
      >
        <span className={styles.triggerLabel}>{selectedLabel}</span>
        <svg viewBox="0 0 20 20" className={styles.chevron} aria-hidden="true" focusable="false">
          <path d="M5.5 8l4.5 4.5L14.5 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <ul
          id={listboxId}
          role="listbox"
          className={mergeClassNames(styles.menu, variant === 'compact' && styles.menuCompact, menuClassName)}
        >
          {options.map((option, index) => {
            const enabledIndex = enabledOptions.findIndex(item => item.value === option.value);
            const isSelected = option.value === value;
            const isActive = enabledIndex >= 0 && enabledIndex === activeIndex;

            return (
              <li key={`${option.value}-${index}`} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  className={mergeClassNames(
                    styles.option,
                    isSelected && styles.optionSelected,
                    isActive && styles.optionActive,
                  )}
                  onMouseDown={event => event.preventDefault()}
                  onMouseEnter={() => {
                    if (!option.disabled && enabledIndex >= 0) {
                      setActiveIndex(enabledIndex);
                    }
                  }}
                  onClick={() => {
                    if (!option.disabled) {
                      commitSelection(option.value);
                    }
                  }}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Select;
